/**
 * useHomeSearch — everything the home page DOES, shared by both looks
 * (screens/HomeScreen.jsx renders both since v1.71; Floodlight only retypes
 * it), so the looks can never drift apart:
 *
 *   - background-query mode (Profile preference) vs. navigating to SgpFlow
 *   - the "Searching for …" overlay state for foreground searches
 *   - the search draft + field reset after a background query is queued
 *   - the signed-in person's 5 most recent saved queries (run / delete)
 *
 * Moved verbatim from HomeScreen.jsx (v1.70); behaviour is unchanged.
 */
import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { collection, query as firestoreQuery, where, getDocs, orderBy, limit, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigation } from '../context/NavigationContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useAuth } from '../context/AuthContext';

/** Example queries the empty search box types out (same list as components/SearchInput.jsx). */
export const SEARCH_SAMPLES = [
  'best nba sgp tonight',
  'lakers -3.5 and lebron 9.5+ ast',
  'under 220.5 and jokic 10+ reb',
  'same game parlay warriors today',
  'nfl sgp chiefs tomorrow',
];

export const sgpRoute = (query) => ['SgpFlow', { payload: { query } }];

/**
 * The typewriter placeholder: types a sample, holds it 2s, deletes it, moves
 * to the next. Runs only while `active` (field empty and not focused). Same
 * timing as components/SearchInput.jsx. Returns '' while idle.
 */
export function useTypewriterPlaceholder(active, samples = SEARCH_SAMPLES, stepMs = 70) {
  const [typed, setTyped] = useState('');
  const sampleIdx = useRef(0);
  const charIdx = useRef(0);
  const direction = useRef(1); // 1 = typing, -1 = deleting, 0 = holding
  const holdTimer = useRef(null);

  useEffect(() => {
    if (!active) {
      setTyped('');
      return undefined;
    }
    const timer = setInterval(() => {
      const cur = samples[sampleIdx.current % samples.length];
      const i = charIdx.current + direction.current;
      if (i <= 0) {
        direction.current = 1;
        sampleIdx.current = (sampleIdx.current + 1) % samples.length;
        charIdx.current = 0;
        setTyped('');
      } else if (i >= cur.length) {
        charIdx.current = cur.length;
        setTyped(cur);
        if (direction.current !== 0) {
          direction.current = 0;
          clearTimeout(holdTimer.current);
          holdTimer.current = setTimeout(() => { direction.current = -1; }, 2000);
        }
      } else {
        charIdx.current = i;
        setTyped(cur.slice(0, i));
      }
    }, stepMs);
    return () => {
      clearInterval(timer);
      clearTimeout(holdTimer.current);
      if (direction.current === 0) direction.current = -1; // never resume stuck on a held sample
    };
  }, [active, samples, stepMs]);

  return typed;
}

export default function useHomeSearch() {
  const { navigateTo } = useNavigation();
  const searchDraftRef = useRef('');
  const [searchFieldResetKey, setSearchFieldResetKey] = useState(0);
  const { user, userProfile } = useAuth();
  const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
  const [savedQueries, setSavedQueries] = useState([]);
  const [showSavedQueries, setShowSavedQueries] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // Loading state for search
  const [searchQuery, setSearchQuery] = useState(''); // Current query being searched

  // Check if background query mode is enabled
  const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
  const queueInBackground = backgroundQueryMode && canAddBackgroundQuery;

  // Debug: Log background mode setting (can remove after debugging)
  useEffect(() => {
    if (userProfile) {
      console.log('[HomeScreen] backgroundQueryMode:', backgroundQueryMode, 'preferences:', userProfile?.preferences);
    }
  }, [userProfile, backgroundQueryMode]);

  // Fetch saved queries
  useEffect(() => {
    const fetchSavedQueries = async () => {
      if (!user?.uid) {
        setSavedQueries([]);
        return;
      }

      try {
        const q = firestoreQuery(
          collection(db, 'saved_queries'),
          where('uid', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const snapshot = await getDocs(q);
        const queries = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSavedQueries(queries);
      } catch (error) {
        console.error('[HomeScreen] Error fetching saved queries:', error);
      }
    };

    fetchSavedQueries();
  }, [user?.uid]);

  // Handle search start (show loading, then navigate) - only for foreground queries
  const handleSearchStart = (query, isBackground = false) => {
    console.log('[HomeScreen] Search starting for:', query, 'background:', isBackground);
    setSearchQuery(query);
    // Only show loading overlay for foreground queries
    if (!isBackground) {
      setIsSearching(true);
    }
    // Navigation will happen after the timeout in SearchInput
  };

  const onValueChange = (t) => { searchDraftRef.current = t; };

  /** SearchInput's onSearchStart. */
  const onSearchStart = (q) => handleSearchStart(q, queueInBackground);

  /** SearchInput's onExecuteSearch: queue it, or open the results. */
  const executeSearch = async (q) => {
    // Check if background query mode is enabled
    if (queueInBackground) {
      // Just add to background queue - no loading overlay.
      // Do NOT hardcode league/sport defaults — backend classifyQuery()
      // detects the league from the prompt (e.g. "nhl fd sgps" → nhl).
      // Hardcoding 'nba' here caused cross-league contamination in
      // the SGP cache shortcut path.
      await addBackgroundQuery(q, {});
      searchDraftRef.current = '';
      setSearchFieldResetKey((k) => k + 1);
    } else {
      // Navigate immediately — SgpFlow enters loading state on mount (no empty flash).
      navigateTo(...sgpRoute(q));
    }
  };

  /**
   * Submitting the field (Enter or the search icon): exactly SearchInput's
   * handleSubmit — trim, remember the query for SgpFlow, start, execute.
   */
  const submitQuery = (raw) => {
    const q = (raw || '').trim();
    if (!q) return;
    try {
      if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', q);
    } catch {}
    onSearchStart(q);
    executeSearch(q);
  };

  /** The Classic "→ Search" button under the field: runs the current draft. */
  const submitDraft = async () => {
    const q = (searchDraftRef.current || '').trim();
    if (q) {
      // Check if background query mode is enabled
      if (queueInBackground) {
        // Same reasoning as above — no hardcoded league/sport.
        await addBackgroundQuery(q, {});
        searchDraftRef.current = '';
        setSearchFieldResetKey((k) => k + 1);
      } else {
        try { if (Platform.OS === 'web') window.sessionStorage.setItem('sgp:lastQuery', q); } catch {}
        navigateTo(...sgpRoute(q));
      }
    }
  };

  const toggleSavedQueries = () => setShowSavedQueries(!showSavedQueries);

  const runSavedQuery = async (sq) => {
    if (queueInBackground) {
      // Let backend classifyQuery detect league/sport from the prompt.
      await addBackgroundQuery(sq.query, {});
    } else {
      navigateTo(...sgpRoute(sq.query));
    }
  };

  const deleteSavedQuery = async (sq) => {
    try {
      await deleteDoc(doc(db, 'saved_queries', sq.id));
      setSavedQueries(prev => prev.filter(q => q.id !== sq.id));
    } catch (error) {
      console.error('Error deleting query:', error);
    }
  };

  return {
    navigateTo,
    user,
    backgroundQueryMode,
    queueInBackground,
    searchDraftRef,
    searchFieldResetKey,
    onValueChange,
    onSearchStart,
    executeSearch,
    submitQuery,
    submitDraft,
    savedQueries,
    showSavedQueries,
    toggleSavedQueries,
    runSavedQuery,
    deleteSavedQuery,
    isSearching,
    searchQuery,
  };
}
